import {
  Icon,
  TextField,
  DefaultButton,
  getTheme,
  PrimaryButton
} from "@fluentui/react";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";

import { Section, ButtonsWrapper } from "./styles";

import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import {
  PaymentRequest,
  PaymentRequestStatus,
  PaymentRequestTypeProviders
} from "@/common/types/PaymentRequestTypes";
import { formatDateAndTimeToString } from "@/utils/FormatDate";

interface PaymentSectionProps {
  paymentType: PaymentMethodType.PIX | PaymentMethodType.CREDIT_CARD;
  paymentRequest: PaymentRequest;
  handleConfirmPayment: () => void;
  showConfirmPaymentButton: boolean;
}

interface PaymentSectionInfo {
  message: string;
  icon?: string;
  showQrCode?: boolean;
}

export function PaymentSection({
  paymentType,
  paymentRequest,
  handleConfirmPayment,
  showConfirmPaymentButton
}: PaymentSectionProps) {
  const theme = getTheme();
  const isPix = paymentType === PaymentMethodType.PIX;
  const isCreditCard =
    paymentRequest.typeProvider === PaymentRequestTypeProviders.CREDIT_CARD;
  const paymentMethodLabel = isPix ? "Pix" : "Cartão de Crédito";

  const [copyCode, setCopyCode] = useState("Copiar link de Cobrança");

  const formatPaymentSectionInfo = (
    paymentRequest: PaymentRequest
  ): PaymentSectionInfo => {
    const statusConfig: Record<PaymentRequestStatus, PaymentSectionInfo> = {
      [PaymentRequestStatus.COMPLETED]: {
        message:
          paymentRequest.completedAt &&
          `Pagamento confirmado em: ${formatDateAndTimeToString(
            paymentRequest.completedAt
          )}`,
        icon: "CompletedSolid"
      },

      [PaymentRequestStatus.REFUNDED]: {
        message: `Reembolso realizado em: ${formatDateAndTimeToString(
          paymentRequest?.refundedAt
        )}`,
        icon: "RepeatAll"
      },

      [PaymentRequestStatus.REFUND_REQUESTED]: {
        message: "O reembolso será realizado em breve.",
        icon: "Clock"
      },

      [PaymentRequestStatus.WAITING_REFUND_REQUEST]: {
        message: "O reembolso será realizado em breve.",
        icon: "Clock"
      },

      [PaymentRequestStatus.EXPIRED]: {
        message: `O prazo encerrou em: ${formatDateAndTimeToString(
          paymentRequest?.expiresAt
        )}`,
        icon: "RemoveEvent"
      },

      [PaymentRequestStatus.PENDING_INTEGRATION]: {
        message: "Aguardando integração do pagamento.",
        icon: "Clock"
      },

      [PaymentRequestStatus.PROCESSING]: {
        message: isCreditCard
          ? "Encaminhe o link abaixo ao cooperado. Ele abrirá a página para pagamento com cartão de crédito."
          : "Encaminhe o link abaixo ao cooperado. Ele abrirá uma página segura da Coopercompany com as opções para pagamento.",
        showQrCode: isPix
      },

      [PaymentRequestStatus.REFUND_NOT_MADE]: {
        message:
          "Não foi possível realizar o reembolso. Em caso de dúvidas, contate o suporte.",
        icon: "ErrorBadge"
      },

      [PaymentRequestStatus.REFUNDED_PARTIAL]: {
        message: `Reembolso foi realizado parcialmente em: ${formatDateAndTimeToString(
          paymentRequest?.refundedAt
        )}. Em caso de dúvidas, contate o suporte.`,
        icon: "RepeatAll"
      },

      [PaymentRequestStatus.CANCELED]: {
        message: "A cobrança foi cancelada",
        icon: "StatusErrorFull"
      }
    };

    if (
      paymentRequest.requestStatus === PaymentRequestStatus.PROCESSING &&
      !paymentRequest.transactionId
    ) {
      return statusConfig[PaymentRequestStatus.PENDING_INTEGRATION];
    }

    return (
      statusConfig[paymentRequest.requestStatus] ?? {
        message:
          "Não foi possível consultar a cobrança. Por favor, entre em contato com o suporte.",
        icon: "StatusErrorFull"
      }
    );
  };

  const paymentSectionInfo = formatPaymentSectionInfo(paymentRequest);
  const memberPortalUrl = import.meta.env.VITE_MEMBER_PORTAL_URL;
  const transactionId = paymentRequest.transactionId;
  const paymentUrl = isCreditCard
    ? paymentRequest.code
    : `${memberPortalUrl}/payments/${transactionId}`;

  const paymentLink =
    transactionId &&
    paymentRequest.requestStatus === PaymentRequestStatus.PROCESSING
      ? paymentUrl
      : "";

  const handleCopyCode = () => {
    if (!paymentLink) return;

    void navigator.clipboard.writeText(paymentLink);
    setCopyCode("Link de Cobrança copiado!");

    setTimeout(() => {
      setCopyCode("Copiar link de Cobrança");
    }, 2500);
  };

  const responsiveTextFieldStyles = {
    fieldGroup: {
      "@media (min-width: 1280px)": { width: "18rem" },
      "@media (max-width: 1280px)": { maxWidth: "100%" }
    }
  };

  return (
    <Section>
      <strong>Cobrança {paymentMethodLabel}</strong>

      <p style={{ marginBottom: "0", marginTop: "0.25rem", maxWidth: "20rem" }}>
        {paymentSectionInfo.message}
      </p>

      {paymentSectionInfo.showQrCode && paymentRequest.code && (
        <QRCodeCanvas
          value={paymentRequest?.code}
          style={{ marginTop: "1rem" }}
        />
      )}

      {paymentSectionInfo.icon && (
        <Icon
          iconName={paymentSectionInfo.icon}
          style={{ fontSize: "100px", color: theme.palette.themeDark }}
        />
      )}

      {paymentLink && (
        <TextField
          styles={responsiveTextFieldStyles}
          label={`Link de Cobrança ${paymentMethodLabel}`}
          disabled={true}
          readOnly={true}
          value={paymentLink}
        />
      )}

      <ButtonsWrapper>
        {paymentLink && (
          <DefaultButton text={copyCode} onClick={handleCopyCode} />
        )}

        {showConfirmPaymentButton && (
          <PrimaryButton
            text="Verificar status da Cobrança"
            onClick={handleConfirmPayment}
          />
        )}
      </ButtonsWrapper>
    </Section>
  );
}
